import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "../utils/supabaseClient";
import { useCart } from "../context/CartContext";
import { customerStatusLabel, formatMoney } from "../utils/orderHelpers";

const POLL_MS = 2000;
const MAX_TRIES = 15; // about 30 seconds
const NOT_CONFIRMED = ["pending"];
const NOT_SUCCESSFUL = ["failed", "cancelled", "amount_mismatch"];

function StatusIcon({ kind }) {
  const common = {
    viewBox: "0 0 24 24",
    width: 34,
    height: 34,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
    focusable: "false",
  };

  if (kind === "ok") {
    return (
      <svg {...common}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    );
  }
  if (kind === "bad") {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    );
  }
  return (
    <svg {...common} className="os-spin">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export default function OrderSuccess() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();

  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [timedOut, setTimedOut] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const cartCleared = useRef(false);

  // Only used to decide which "receipt" link to offer
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setSignedIn(Boolean(data?.user)));
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setError("We couldn't verify this order. Please check your email or contact us.");
      return;
    }

    let cancelled = false;
    let timer;
    let tries = 0;

    async function check() {
      tries += 1;

      // Goes through an edge function so guests (no login) can see their own order too.
      const { data, error: fnError } = await supabase.functions.invoke(
        "get-order-status",
        { body: { orderId, sessionId } }
      );
      if (cancelled) return;

      if (fnError || !data || data.error) {
        console.error("get-order-status error:", fnError || data);
        setError("We couldn't load your order details. Please check your email or contact us.");
        return;
      }

      setOrder(data);

      // The customer has paid at Stripe, so the cart can be emptied
      if (!cartCleared.current && !NOT_SUCCESSFUL.includes(data.status)) {
        cartCleared.current = true;
        if (typeof clearCart === "function") clearCart();
      }

      // The webhook is the source of truth: keep checking until it has updated the order
      if (NOT_CONFIRMED.includes(data.status)) {
        if (tries >= MAX_TRIES) {
          setTimedOut(true);
          return;
        }
        timer = setTimeout(check, POLL_MS);
      }
    }

    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, sessionId]);

  const confirmed =
    order && !NOT_CONFIRMED.includes(order.status) && !NOT_SUCCESSFUL.includes(order.status);
  const unsuccessful = order && NOT_SUCCESSFUL.includes(order.status);
  const waiting = order && !confirmed && !unsuccessful;

  // ---- Could not load / verify the order ----
  if (error) {
    return (
      <div className="os">
        <div className="os-card">
          <span className="os-icon os-icon--bad"><StatusIcon kind="bad" /></span>
          <h2 className="os-title">Order status</h2>
          <p className="os-lead">{error}</p>
          <div className="os-actions">
            <Link to="/order-lookup" className="ui-btn ui-btn--primary">Find your order</Link>
            <Link to="/contact" className="ui-btn ui-btn--ghost">Contact us</Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Loading the first time ----
  if (!order) {
    return (
      <div className="os">
        <div className="os-card">
          <span className="os-icon os-icon--wait"><StatusIcon kind="wait" /></span>
          <h2 className="os-title">Loading your order</h2>
          <p className="os-lead">One moment please.</p>
        </div>
      </div>
    );
  }

  const items = Array.isArray(order.items) ? order.items : [];

  let iconKind = "wait";
  let title = "Confirming your payment";
  let lead = "Hang tight, this usually takes a few seconds.";

  if (confirmed) {
    iconKind = "ok";
    title = "Thank you for your order";
    lead = `We're sending a confirmation email to ${order.email}. Your order is now being prepared.`;
  } else if (unsuccessful) {
    iconKind = "bad";
    title = "There was a problem with your payment";
    lead = "Please contact us quoting your order number so we can help.";
  } else if (timedOut) {
    lead =
      "This is taking longer than usual. If your payment went through, your order is safe and will be confirmed shortly. Please contact us if it doesn't update.";
  }

  return (
    <div className="os">
      <div className="os-card">
        <span className={`os-icon os-icon--${iconKind}`}><StatusIcon kind={iconKind} /></span>
        <h2 className="os-title">{title}</h2>
        <p className="os-lead" aria-live="polite">{lead}</p>

        <div className="os-summary">
          <dl className="os-kv">
            <dt>Order number</dt>
            <dd><strong>#{order.id}</strong></dd>
            <dt>Email</dt>
            <dd>{order.email}</dd>
            <dt>Status</dt>
            <dd>
              <span className={`ui-badge ui-status--${order.status}`}>
                {customerStatusLabel(order.status)}
              </span>
            </dd>
          </dl>

          <ul className="os-items">
            {items.map((item, i) => (
              <li key={i} className="os-item">
                <span>
                  {item.name} × {item.qty}
                </span>
                {item.price != null && (
                  <span>{formatMoney(Number(item.price) * Number(item.qty))}</span>
                )}
              </li>
            ))}
          </ul>

          {Number(order.delivery_fee) > 0 && (
            <div className="os-delivery">
              <span>Delivery</span>
              <span>{formatMoney(order.delivery_fee)}</span>
            </div>
          )}

          <div className="os-total">
            <span>{confirmed ? "Total paid" : "Total"}</span>
            <span>{formatMoney(order.total)}</span>
          </div>
        </div>

        {confirmed && !signedIn && (
          <p className="os-note">
            Keep your order number. You can look up this order and download a receipt at any time
            using "Find your order" at the bottom of the site.
          </p>
        )}

        <div className="os-actions">
          {confirmed && signedIn && (
            <Link to={`/receipt/${order.id}`} className="ui-btn ui-btn--primary">
              View receipt
            </Link>
          )}
          {confirmed && !signedIn && (
            <Link to="/order-lookup" className="ui-btn ui-btn--primary">
              Find your order
            </Link>
          )}
          {unsuccessful && (
            <Link to="/contact" className="ui-btn ui-btn--primary">Contact us</Link>
          )}
          {waiting && timedOut && (
            <Link to="/contact" className="ui-btn ui-btn--primary">Contact us</Link>
          )}
          <Link to="/products" className="ui-btn ui-btn--ghost">Continue shopping</Link>
          <Link to="/" className="ui-btn ui-btn--ghost">Back to home</Link>
        </div>
      </div>
    </div>
  );
}